<template>
    <v-card class="mb-5">
        <v-card-text>
            <v-row>
                <v-col cols="12" md="5">
                    <tour-general-info :info="info" :package-tour-info="packageTourInfo" />
                </v-col>
                <v-col cols="12" md="2">
                    <div v-for="traveler in travelers" :key="traveler.personId">
                        <v-icon start size="small">mdi-account</v-icon>
                        <div>
                            <span class="text-uppercase" v-html="getTravelerName(traveler)" />
                            <small>{{ traveler.citizenshipName }}</small>
                            <div v-if="traveler.passports && traveler.passports.length" class="text-caption">
                                {{ $t('document') }} {{ traveler.passports[0].number }}<br />
                                {{ $t('expire_date') }} {{ traveler.passports[0].expiryDate }}
                            </div>
                        </div>
                    </div>
                </v-col>
                <v-col cols="12" md="3" class="justify-center justify-md-end">
                    <order-prices
                        v-if="!isReadOnly || isTripAssistantKeyExist"
                        :supplier-cancel-penalties="order.orderPackage.supplierCancelPenalties"
                        :supplier-payment-status="order.supplierPaymentStatus"
                        :supplier-price="order.orderPackage.supplierPrice"
                        :client-cancel-penalties="order.orderPackage.clientCancelPenalties"
                        :customer-payment-status="order.customerPaymentStatus"
                        :client-price="
                            customComplexTourInfo ? clientCustomComplexTourPrice : order.orderPackage.clientPrice
                        "
                    />
                </v-col>
                <v-col
                    v-if="(isShowBookButton || isShowCancelButton) && !isReadOnly"
                    cols="12"
                    md="2"
                    class="d-flex flex-column align-md-end justify-center"
                >
                    <v-btn v-if="isShowBookButton" class="mb-3" variant="flat" color="primary" @click="bookAllQuote">
                        {{ $t('book') }}
                    </v-btn>
                    <a
                        v-if="customComplexTourInfo"
                        class="mb-3 cursor-pointer text-black modify-link"
                        @click="modal = true"
                    >
                        {{ $t('manual_correction') }}
                    </a>
                    <v-btn v-if="isShowCancelButton" class="mt-2" variant="flat" @click="cancelTour">
                        {{ $t('cancel') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
    <ModifyManualClientPriceModal v-model="modal" :service="customComplexTourInfo" />
</template>

<script lang="ts">
    import OrderPrices from '~src/components/account/snippets/orderPrices.src'
    import TourGeneralInfo from '@/components/tours/snippets/TourGeneralInfo.vue'
    import {Component, Emit, Inject, Prop, Vue} from 'vue-facing-decorator'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'
    import ModifyManualClientPriceModal from '~/components/account/modify/manual/ModifyManualClientPriceModal.vue'

    @Component({
        components: {
            ModifyManualClientPriceModal,
            TourGeneralInfo,
            OrderPrices,
            OrderInfoBase,
        },
    })
    export class PackageTourInfo extends Vue {
        @Prop() order
        @Inject({default: false}) isReadOnly
        @Inject({default: false}) isTripAssistantKeyExist

        packageTourInfo = null
        modal = null

        @Emit()
        cancelTour() {}

        @Emit()
        bookAllQuote() {
            return this.packageServices
        }

        async mounted() {
            try {
                this.packageTourInfo = await this.$api.packageTourInfo.get({
                    packageTourId: this.order.orderPackage.packageId,
                })
            } catch (e) {
                this.packageTourInfo = null
            }
        }

        get customComplexTourInfo() {
            return this.order.services?.find(({serviceType}) => serviceType === 'CUSTOMCOMPLEXTOUR')
        }

        get clientCustomComplexTourPrice() {
            return this.customComplexTourInfo?.salesTerms?.find(({type}) => type === 'CLIENT')?.price
        }

        get isShowCancelButton() {
            return this.packageServices.every(
                s =>
                    !!s.cancellationAllowed ||
                    (s.status === 'Confirmation pending' && this.$conf.public.account.cancelConfirmationPendingService)
            )
        }

        get isShowBookButton() {
            return this.packageServices.every(s => s.status === 'Quote')
        }

        get packageServices() {
            return this.order.services.filter(s => !!s.serviceContainerId)
        }

        get info() {
            const tariffName = this.customComplexTourInfo?.serviceDetails[0].tariffName
            const serviceName = this.customComplexTourInfo?.serviceDetails[0].serviceName
            const secondaryServiceName = this.customComplexTourInfo?.serviceDetails[0].secondaryServiceName

            const startDates = this.packageServices?.map(service => {
                return new Date(service.startDateTime)
            })
            const minDate = startDates.sort((a, b) => a - b)[0]

            const endDates = this.packageServices?.map(service => {
                return new Date(service.endDateTime)
            })
            const maxDate = endDates.sort((a, b) => a - b)[endDates.length - 1]

            const packageDuration = this.$dateFns.differenceInCalendarDays(new Date(maxDate), new Date(minDate)) + 1

            return {
                packageDuration,
                packageDate: this.$dateFns.format(minDate, 'yyyy-MM-dd HH:mm'),
                tariffName,
                serviceName,
                secondaryServiceName,
            }
        }

        get travelers() {
            return this.packageServices.travelers
        }

        getTravelerName(traveler) {
            if (traveler.prefix) return `${traveler.prefix} ${traveler.name[0].firstName} ${traveler.name[0].lastName}`
            return `${traveler.name[0].firstName} ${traveler.name[0].lastName}`
        }
    }

    export default PackageTourInfo
</script>
